//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TikTokMetricSortKey } from '@apis/tiktok-analytics/tiktokAnalyticsApiModel';
import {
  deleteScheduledPosts,
  searchScheduledPosts,
} from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApi';
import { UniversalPostingGroupName } from '@apis/universal-posts/universalPostsApiModel';
import {
  getAliasedDateRangeFromUnisonDateRange,
  getAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import PostableId from '@models/PostableId';
import PostableStatus from '@models/PostableStatus';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUnisonDateRange } from '@redux/selectors/getSessionInfo';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { TikTokSummaryPerformanceOption } from '@tiktok/components/summary/tiktokSummaryConstants';
import {
  fetchAudienceDailyStats,
  fetchAudienceSummaryStats,
} from '@tiktok/redux/audience/tiktokAudienceSlice';
import {
  fetchEngagementMetrics,
  fetchUserMedia,
} from '@tiktok/redux/engagement/tiktokEngagementSlice';
import {
  getTikTokIsDeletingUpcomingScheduledPost,
  getTikTokIsFetchingUpcomingScheduledPosts,
} from '@tiktok/redux/summary/selectors/getTikTokSummary';
import {
  ITikTokSummaryState,
  NormalizedScheduledPosts,
  UPCOMING_SCHEDULED_POSTS_COUNT,
} from '@tiktok/redux/summary/tiktokSummaryModel';
import {
  getTikTokUserDisplayName,
  getTikTokUserId,
  getTikTokUsername,
} from '@tiktok/redux/user/selectors/getTikTokUser';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import normalize, { Normalized } from '@utils/normalize';

const initialState: ITikTokSummaryState = {
  currentGraphMetric: TikTokSummaryPerformanceOption.FollowerCount,
  upcomingScheduledPosts: Normalized.empty(),

  flags: {
    isDeletingUpcomingScheduledPost: false,
    isFetchingUpcomingScheduledPosts: false,
  },
  errors: {
    deleteUpcomingScheduledPostError: undefined,
    fetchUpcomingScheduledPostsError: undefined,
  },
  modals: {
    selectedPostIdForDeleteUpcomingScheduledPostModal: undefined,
    isDeleteUpcomingScheduledPostModalOpen: false,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TikTokSummary,
  initialState,
  reducers: {
    requestUpcomingScheduledPosts(state) {
      state.errors.fetchUpcomingScheduledPostsError = undefined;
      state.flags.isFetchingUpcomingScheduledPosts = true;
    },
    receiveUpcomingScheduledPostsSucceeded(
      state,
      action: PayloadAction<NormalizedScheduledPosts>,
    ) {
      state.flags.isFetchingUpcomingScheduledPosts = false;
      state.upcomingScheduledPosts = action.payload;
    },
    receiveUpcomingScheduledPostsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingUpcomingScheduledPosts = false;
      state.errors.fetchUpcomingScheduledPostsError = action.payload;
    },
    requestDeleteUpcomingScheduledPost(state) {
      state.errors.deleteUpcomingScheduledPostError = undefined;
      state.flags.isDeletingUpcomingScheduledPost = true;
    },
    receiveDeleteUpcomingScheduledPostSucceeded(state) {
      state.flags.isDeletingUpcomingScheduledPost = false;
    },
    receiveDeleteUpcomingScheduledPostFailed(
      state,
      action: PayloadAction<string>,
    ) {
      state.flags.isDeletingUpcomingScheduledPost = false;
      state.errors.deleteUpcomingScheduledPostError = action.payload;
    },
    openDeleteUpcomingScheduledPostModal(
      state,
      action: PayloadAction<PostableId>,
    ) {
      state.modals.isDeleteUpcomingScheduledPostModalOpen = true;
      state.modals.selectedPostIdForDeleteUpcomingScheduledPostModal =
        action.payload;
    },
    closeDeleteUpcomingScheduledPostModal(state) {
      state.modals.isDeleteUpcomingScheduledPostModalOpen = false;
      state.modals.selectedPostIdForDeleteUpcomingScheduledPostModal =
        undefined;
    },
    updateGraphMetric(
      state,
      action: PayloadAction<TikTokSummaryPerformanceOption>,
    ) {
      state.currentGraphMetric = action.payload;
    },
  },
});

export const {
  closeDeleteUpcomingScheduledPostModal,
  openDeleteUpcomingScheduledPostModal,
  updateGraphMetric,
} = actions;

/**
 * This fetches the 8 most recent videos, sorted by posted date.
 * Used on the Summary page.
 */
export const fetchSummaryContent = createThunk(() => (dispatch, getState) => {
  const state = getState();
  const tiktokUserId = getTikTokUserId(state);

  const tiktokUsername = getTikTokUsername(state);
  const tiktokDisplayName = getTikTokUserDisplayName(state);
  const userTimeZoneId = getUserTimeZone(state);

  const dateRange = getAliasedDateRange('last7Days', userTimeZoneId);

  // If either the tiktokUserId or tiktokUsername is undefined, do not attempt to fetch user media
  if (!isDefined(tiktokUserId) || !isDefined(tiktokUsername)) {
    return;
  }

  void dispatch(fetchAudienceDailyStats(dateRange));
  void dispatch(fetchAudienceSummaryStats(dateRange));
  void dispatch(fetchEngagementMetrics(dateRange));
  void dispatch(
    fetchUserMedia({
      tiktokUsername,
      tiktokDisplayName,
      tiktokUserId,
      endTime: dateRange.endDate,
      limit: 8,
      offset: 0,
      sortKey: TikTokMetricSortKey.PostedTimestamp,
      isOrderAscending: false,
    }),
  );
});

/**
 * This fetches the top 8 videos, sorted by engagement rate, for the summary pdf export.
 * The date range is selected by the user.
 */
export const fetchSummaryContentForExport = createThunk(
  () => (dispatch, getState) => {
    const state = getState();
    const tiktokUserId = getTikTokUserId(state);

    const tiktokUsername = getTikTokUsername(state);
    const tiktokDisplayName = getTikTokUserDisplayName(state);
    const unisonDateRange = getUnisonDateRange(state);
    const userTimeZoneId = getUserTimeZone(state);

    // If either the tiktokUserId or tiktokUsername is undefined, do not attempt to fetch user media
    if (!isDefined(tiktokUserId) || !isDefined(tiktokUsername)) {
      return;
    }

    const aliasedDateRange = getAliasedDateRangeFromUnisonDateRange(
      unisonDateRange,
      userTimeZoneId,
    );

    void dispatch(fetchAudienceDailyStats(aliasedDateRange));
    void dispatch(fetchAudienceSummaryStats(aliasedDateRange));
    void dispatch(fetchEngagementMetrics(aliasedDateRange));
    void dispatch(
      fetchUserMedia({
        tiktokUsername,
        tiktokDisplayName,
        tiktokUserId,
        startTime: aliasedDateRange.startDate,
        endTime: aliasedDateRange.endDate,
        limit: 8,
        offset: 0,
        sortKey: TikTokMetricSortKey.EngagementRate,
        isOrderAscending: false,
      }),
    );
  },
);

export const getUpcomingScheduledPosts = createThunk(
  () => async (dispatch, getState) => {
    const isFetchingScheduledPosts = getTikTokIsFetchingUpcomingScheduledPosts(
      getState(),
    );

    if (isFetchingScheduledPosts) {
      return;
    }

    dispatch(actions.requestUpcomingScheduledPosts());

    try {
      const { data: upcomingScheduledPosts } = await searchScheduledPosts({
        groups: [UniversalPostingGroupName.TikTokVideoAutomaticGroup],
        status: [PostableStatus.Scheduled, PostableStatus.Rescheduled],
        after: new Date(),
        limit: UPCOMING_SCHEDULED_POSTS_COUNT,
      });

      dispatch(
        actions.receiveUpcomingScheduledPostsSucceeded(
          normalize(upcomingScheduledPosts, (post) => post.id),
        ),
      );
    } catch (e) {
      dispatch(
        actions.receiveUpcomingScheduledPostsFailed(
          getRequestErrorMessage(e) ??
            'Unknown error while searching scheduled posts',
        ),
      );
    }
  },
);

export const deleteUpcomingScheduledPost = createThunk(
  (postId: PostableId) => async (dispatch, getState) => {
    const isDeletingUpcomingScheduledPost =
      getTikTokIsDeletingUpcomingScheduledPost(getState());

    if (isDeletingUpcomingScheduledPost) {
      return;
    }

    dispatch(actions.requestDeleteUpcomingScheduledPost());

    try {
      await deleteScheduledPosts([postId]);

      dispatch(actions.receiveDeleteUpcomingScheduledPostSucceeded());

      // Once we successfully delete the post, we want to refresh the upcoming posts on the summary page
      await dispatch(getUpcomingScheduledPosts());

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Deleted Post',
          type: ToastType.SUCCESS,
          message: `Your post has been deleted.`,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    } catch (e) {
      dispatch(
        actions.receiveDeleteUpcomingScheduledPostFailed(
          getRequestErrorMessage(e) ??
            `Unknown error while attempting to delete post ${postId}`,
        ),
      );

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Unable to Delete Post',
          type: ToastType.ERROR,
          message:
            'We were unable to delete this post. Please try again later.',
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    }
  },
);

export default reducer;
